import { useContext } from 'react'

import { datadogLogs } from '@datadog/browser-logs'
import { OptimizelyContext } from '@optimizely/react-sdk'
import Cookies from 'js-cookie'

import { getAuthToken } from '@library/auth'
import { canUseWindow } from '@library/environment'

const EXPERIMENT_KEY = 'beetroots_enable_box_price_calculator'
const TREATMENT_VARIANT = 't1'

/**
 * Bucketing is a client-side decision keyed on the client-id cookie, not sticky assignment, so
 * it holds per browser but not across devices or a cookie clear (see rollout.md).
 *
 * Duplicates `useIsTaperedDiscountCurveEnabled` on purpose: sharing a helper would change the
 * code path allocating that experiment, which is live on this same page.
 */
export const useIsBoxPriceCalculatorEnabled = (): boolean => {
  const { optimizely } = useContext(OptimizelyContext)

  if (!canUseWindow()) {
    return false
  }

  const authToken = getAuthToken()
  const isAuthenticated = !!authToken

  let hasAcceptedAnalytics = false
  try {
    const consentCookieString = Cookies.get('v1_gousto_cookie_consent')
    const consentCookie = consentCookieString ? JSON.parse(consentCookieString) : {}
    hasAcceptedAnalytics = consentCookie?.categories?.includes('ANALYTICS')
  } catch (error) {
    if (error instanceof Error) {
      datadogLogs.logger.error('Error parsing box price calculator cookie consent', {
        error: error.message,
        cookieConsent: Cookies.get('v1_gousto_cookie_consent'),
      })
    }
  }

  // Logged-out acquisition surface, so allocation needs analytics consent and no auth token.
  let enabled = false
  let variationKey: string | null | undefined

  if (optimizely && hasAcceptedAnalytics && !isAuthenticated) {
    try {
      const decision = optimizely.decide(EXPERIMENT_KEY)
      enabled = decision.enabled
      variationKey = decision.variationKey
    } catch (error) {
      datadogLogs.logger.error('Error deciding box price calculator', {
        error: error instanceof Error ? error.message : 'Unknown error',
      })
    }
  }

  return enabled && variationKey === TREATMENT_VARIANT
}
